/**
 * Translation provider.
 *
 * The catalogue is fetched from the API — which reads it from the database —
 * and cached by TanStack Query. No copy is compiled into this bundle, so
 * changing a word or adding a language is a database change, not a release.
 *
 * Fetching through `useQuery` rather than an effect is deliberate: loading and
 * error states come for free, and there is no state to synchronise by hand.
 */
import { type ReactNode, use, useEffect, useMemo } from 'react';

import { createTranslator, type Locale, msg, type TranslationCatalogue } from '@daka/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { I18nProvider as AriaI18nProvider } from 'react-aria-components';

import { useDeviceLocale } from '@/i18n/device-locale';
import { I18nContext, type I18nValue } from '@/i18n/useT';
import { i18n } from '@/lib/api';
import { unwrap } from '@/lib/http';
import { queryKeys } from '@/lib/queryKeys';

/** A catalogue that failed to load: keys render as-is, and each miss is reported. */
const EMPTY: TranslationCatalogue = {};

/**
 * Catalogue for `locale`, including the caller's tenant overrides — so the
 * cache is keyed by organisation too, never shared across tenants.
 *
 * Until it lands, a nested provider keeps the enclosing catalogue and the root
 * renders nothing (one small request): users never see raw keys, and a key is
 * only reported missing when a loaded catalogue really lacks it.
 */
export function I18nProvider({
   locale: sessionLocale,
   organizationId = null,
   children
}: {
   /** The signed-in user's; without one (the root, before sign-in) the device's. */
   locale?: Locale;
   organizationId?: string | null;
   children: ReactNode;
}) {
   const deviceLocale = useDeviceLocale();
   const wanted = sessionLocale ?? deviceLocale;
   const enclosing = use(I18nContext);
   const { data, isPending } = useQuery({
      queryKey: queryKeys.catalogue(wanted, organizationId),
      queryFn: async () => ({ locale: wanted, catalogue: await unwrap(i18n[':locale'].$get({ param: { locale: wanted } })) }),
      // Copy changes rarely; re-fetching it on every mount would be wasteful.
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      // Without a catalogue every screen shows raw keys, so keep trying through an API restart.
      retry: 5,
      // Switching language keeps the current copy on screen until the next arrives,
      // rather than unmounting the app mid-action (a sign-in would lose its navigation).
      placeholderData: keepPreviousData
   });

   // Everything — copy, `<html lang>`, date formats — follows the catalogue on screen.
   const locale = data?.locale ?? wanted;
   const catalogue = data?.catalogue;
   const value = useMemo<I18nValue>(() => {
      const translate = createTranslator(locale, catalogue ?? EMPTY);
      return {
         locale,
         t: (target, params) => (typeof target === 'string' ? translate(msg(target, params)) : translate(target))
      };
   }, [locale, catalogue]);

   // Until this catalogue first arrives, keep the enclosing one on screen — through the
   // same element tree: swapping bare children for wrapped ones would remount the app
   // and wipe whatever the user was typing.
   const shown = isPending ? enclosing : value;

   // Screen readers, hyphenation and the browser's own controls follow the page language.
   useEffect(() => {
      if (shown) document.documentElement.lang = shown.locale;
   }, [shown]);

   // The root has nothing to show before its first catalogue (one small request).
   if (!shown) return null;
   // React Aria formats dates, numbers and calendars (week start, month names) for the same locale.
   return (
      <I18nContext value={shown}>
         <AriaI18nProvider locale={shown.locale}>{children}</AriaI18nProvider>
      </I18nContext>
   );
}
